// Diary — upcoming events. Pulls posts of type='event' from the API and
// orders by starts_at ascending. Past events fall to the bottom.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PostCard } from '../components/PostCard';
import { formatMastheadDate } from '../lib/format';
import type { Post } from '../types/api';

type DiaryFilter = 'all' | 'mine';

export function Diary() {
    const api = useApi();
    const { authed, login } = useAuth();
    const [events, setEvents] = useState<Post[]>([]);
    const [filter, setFilter] = useState<DiaryFilter>('all');

    const load = useCallback(async () => {
        if (!authed) return;
        try {
            // "Mine" hits /api/me/rsvps which JOINs event_rsvps→posts, so we
            // get only events the user has said "going" or "maybe" to. "All"
            // walks the paper's post list and filters client-side (POC scale).
            const url = filter === 'mine' ? '/api/me/rsvps' : '/api/posts?limit=100';
            const r = await api.get<{ posts: Post[] }>(url);
            const evts = (r.posts ?? []).filter(p => p.post_type === 'event');
            evts.sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
            setEvents(evts);
        } catch (e) { console.error('diary load failed', e); }
    }, [api, authed, filter]);

    useEffect(() => { load(); }, [load]);

    const now = new Date().toISOString();
    const upcoming = useMemo(() => events.filter(e => (e.starts_at ?? '') >= now), [events, now]);
    const past     = useMemo(() => events.filter(e => (e.starts_at ?? '') <  now).reverse(), [events, now]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see what's on this week.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );

    return (
        <main>
            <div className="paper-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>Diary</h1>
                    <div className="issue">{formatMastheadDate()}</div>
                    <div className="rule-bottom" />
                </div>

                <div className="queue-filter" style={{ justifyContent: 'center', margin: '16px 0' }}>
                    <button type="button" className={filter === 'all'  ? 'is-active' : ''} onClick={() => setFilter('all')}>All events</button>
                    <button type="button" className={filter === 'mine' ? 'is-active' : ''} onClick={() => setFilter('mine')}>My events</button>
                </div>

                {events.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#666', padding: '40px 24px', fontStyle: 'italic', fontFamily: "'Newsreader',serif" }}>
                        No events yet. Hit the "+" up top and pick <strong>Event</strong> to add one.
                    </p>
                ) : (
                    <>
                        <section>
                            <h2 className="section-head">Upcoming</h2>
                            {upcoming.length === 0
                                ? <p style={{ color: '#999', fontStyle: 'italic' }}>Nothing scheduled yet.</p>
                                : (
                                    <div className="cards-cols">
                                        {upcoming.map(e => <PostCard key={e.id} post={e} />)}
                                    </div>
                                )}
                        </section>
                        {past.length > 0 && (
                            <section style={{ marginTop: 24 }}>
                                <h2 className="section-head">Past</h2>
                                <div className="cards-cols">
                                    {past.map(e => <PostCard key={e.id} post={e} />)}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </main>
    );
}
